import React, { useState, useEffect, useRef, useContext } from 'react';
import { ChevronDownIcon, XIcon, SearchIcon } from 'lucide-react';
import { Context } from 'services';

const Dropdown = ({
  refresh,
  dataSource,
  defaultString,
  searchFormField,
  labelBaseProps,
  pickerSelectSimpleClasses,
  onChange,
  field,
  valueKey,
  multiple,
  enableClear,
  enableClose,
  enableSearch,
  disabled
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { searchForm }: any = useContext(Context);

  useEffect(() => {
    const fetchOptions = async () => {
      const data = await dataSource(searchFormField ? searchFormField.label : '');
      setOptions(data);
      setFilteredOptions(data);
    };
    fetchOptions();
  }, [dataSource, searchFormField, refresh]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleSelect = (option: any) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setFilteredOptions(options);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    searchForm.setField(field, "");
    setSearchTerm('');
    setFilteredOptions(options);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    const filtered = options.filter((option: any) => 
      option.label.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredOptions(filtered);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleSelect(filteredOptions[0]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className={`block ${labelBaseProps.labelBaseClasses.font} mb-1`}>
        {labelBaseProps.label}
      </label>
      <div
        className={`relative border border-gray-300 rounded-md ${
          disabled ? 'cursor-not-allowed' : 'cursor-text'
        }`}
        onClick={handleToggle}
      >
        <input
          ref={inputRef}
          type="text"
          className={`w-full pr-10 py-2 px-3 rounded-md border-0 ${
            searchTerm || (searchFormField && searchFormField.label)
              ? pickerSelectSimpleClasses.selectedDataClasses.spanBaseClasses
              : pickerSelectSimpleClasses.selectedDataClasses.placeholderClasses
          } ${
            disabled ? 'bg-gray-100' : 'bg-white'
          } focus:outline-none`}
          placeholder={defaultString}
          value={searchTerm || (searchFormField ? searchFormField.label : '')}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          {(searchTerm || (searchFormField && searchFormField.label)) ? (
            <XIcon
              className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={handleClear}
            />
          ) : (
            <SearchIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>
      {isOpen && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-400 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions?.map((option: any, index) => (
            <li
              key={index}
              className={`p-2 hover:bg-gray-100 cursor-pointer ${
                searchFormField && searchFormField[valueKey] === option[valueKey] ? 'bg-blue-100' : ''
              }`}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;